// LOAD-BEARING SIDE-EFFECT IMPORT — do not delete as "unused". It restores the
// Rematch model-cluster init order the pre-carve `@/pages/api/scribeAPIV2`
// import performed as a side effect; the sibling carves on this door measured
// eleven suites dying without it. It retires with the Z2 codemod.
import '@/pages/api/scribeAPIV2';

import { useIntegrationEhrInfo } from '@/hooks/integrations/useIntegrationEhrInfo';
import { useOldFrameworkIntegrations } from '@/hooks/integrations/useOldFrameworkIntegrations';
import { useQueryKindeProperties } from '@/hooks/query/useQueryKindeProperties';
import { useQueryUser } from '@/hooks/query/useQueryUser';
import { useIsAuthenticated } from '@/hooks/useIsAuthenticated';
import {
  useQueryIntegrationLinkSessionInfoByCaseId as useQueryIntegrationLinkSessionInfoByCaseIdCore,
  useQueryIntegrationLinkSessionInfo as useQueryIntegrationLinkSessionInfoCore,
} from '@heidi/integrations/ehr/data';
import { type EHRProvider } from '@heidi/schema/ml-scribe';
import { useQuerySessionById } from '@heidi/session/data';

export function useQueryIntegrationLinkSessionInfo({ sessionId }: { sessionId: string }) {
  const { isAuthenticated } = useIsAuthenticated();
  const { data: kindeProperties } = useQueryKindeProperties();
  const user = useQueryUser((data) => data.kinde_user_id);
  const oldFrameworkIntegrations = useOldFrameworkIntegrations();

  const { isIntegrationOnboardingSession } = useIntegrationEhrInfo();
  const { data: sessionPatientEhrProvider } = useQuerySessionById({
    sessionId: sessionId,
    select: (data) => data?.patient_profile?.ehr,
  });

  const isQueryable = Boolean(
    isAuthenticated &&
    kindeProperties?.region &&
    user &&
    isIntegrationOnboardingSession &&
    sessionId &&
    !oldFrameworkIntegrations.includes(sessionPatientEhrProvider as EHRProvider)
  );

  return useQueryIntegrationLinkSessionInfoCore({ sessionId, isQueryable });
}

export const useQueryIntegrationLinkSessionInfoByCaseId = ({
  caseId,
}: {
  caseId?: string;
}) => {
  const { isAuthenticated } = useIsAuthenticated();
  const { data: kindeProperties } = useQueryKindeProperties();
  const user = useQueryUser((data) => data.kinde_user_id);

  return useQueryIntegrationLinkSessionInfoByCaseIdCore({
    caseId,
    isQueryable: Boolean(isAuthenticated && kindeProperties?.region && user && caseId),
  });
};
